import { CoinIcon } from '../components/ui/CoinIcon';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  ChevronRight, ChevronLeft, FileText, CheckCircle, Zap,
  BookOpen, Calendar, ClipboardList, LayoutGrid, Clock, Info, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COIN_COST = { 'Modul Ajar': 8, KKTP: 5, Promes: 5, Prota: 4, Jadwal: 3 };

const DOC_TYPES = [
  {
    value: 'Modul Ajar',
    label: 'Modul Ajar',
    fullName: 'Modul Ajar',
    desc: 'Dokumen pembelajaran lengkap untuk 1 pertemuan, 1 mata pelajaran. Mencakup identitas, langkah pembelajaran, asesmen, LKPD, dan refleksi.',
    icon: BookOpen,
    color: 'purple',
    time: '~5 menit',
  },
  {
    value: 'KKTP',
    label: 'KKTP',
    fullName: 'Kriteria Ketercapaian Tujuan Pembelajaran',
    desc: 'Rubrik dan kriteria penilaian untuk mengukur ketercapaian tujuan pembelajaran.',
    icon: ClipboardList,
    color: 'green',
    time: '~2 menit',
  },
  {
    value: 'Promes',
    label: 'Promes',
    fullName: 'Program Semester',
    desc: 'Distribusi materi pelajaran dalam satu semester beserta alokasi waktunya.',
    icon: Calendar,
    color: 'orange',
    time: '~3 menit',
  },
  {
    value: 'Prota',
    label: 'Prota',
    fullName: 'Program Tahunan',
    desc: 'Rencana distribusi materi selama satu tahun ajaran penuh.',
    icon: LayoutGrid,
    color: 'pink',
    time: '~3 menit',
  },
  {
    value: 'Jadwal',
    label: 'Jadwal',
    fullName: 'Jadwal Pembelajaran',
    desc: 'Jadwal kegiatan pembelajaran harian/mingguan yang terstruktur.',
    icon: Clock,
    color: 'cyan',
    time: '~2 menit',
  },
];

const JENJANG_KELAS = {
  SD:  [1, 2, 3, 4, 5, 6],
  SMP: [7, 8, 9],
  SMA: [10, 11, 12],
  SMK: [10, 11, 12],
};

const FASE_MAP = {
  SD:  { 1: 'A', 2: 'A', 3: 'B', 4: 'B', 5: 'C', 6: 'C' },
  SMP: { 7: 'D', 8: 'D', 9: 'D' },
  SMA: { 10: 'E', 11: 'F', 12: 'F' },
  SMK: { 10: 'E', 11: 'F', 12: 'F' },
};

const FASE_DESC = {
  A: 'Kelas 1–2 SD',
  B: 'Kelas 3–4 SD',
  C: 'Kelas 5–6 SD',
  D: 'Kelas 7–9 SMP',
  E: 'Kelas 10 SMA/SMK',
  F: 'Kelas 11–12 SMA/SMK',
};

const getFase = (jenjang, kelas) => FASE_MAP[jenjang]?.[kelas] || 'D';

const COLOR_MAP = {
  blue:   { card: 'border-blue-300 bg-blue-50',   icon: 'bg-blue-100 text-blue-600',   badge: 'bg-blue-100 text-blue-700' },
  purple: { card: 'border-purple-300 bg-purple-50', icon: 'bg-purple-100 text-purple-600', badge: 'bg-purple-100 text-purple-700' },
  green:  { card: 'border-green-300 bg-green-50', icon: 'bg-green-100 text-green-600', badge: 'bg-green-100 text-green-700' },
  orange: { card: 'border-orange-300 bg-orange-50', icon: 'bg-orange-100 text-orange-600', badge: 'bg-orange-100 text-orange-700' },
  pink:   { card: 'border-pink-300 bg-pink-50',   icon: 'bg-pink-100 text-pink-600',   badge: 'bg-pink-100 text-pink-700' },
  cyan:   { card: 'border-cyan-300 bg-cyan-50',   icon: 'bg-cyan-100 text-cyan-600',   badge: 'bg-cyan-100 text-cyan-700' },
};

const PRAKTIK_PEDAGOGIS_OPTIONS = [
  'Discovery Learning',
  'Problem Based Learning (PBL)',
  'Project Based Learning (PjBL)',
  'Inquiry Learning',
  'Cooperative Learning',
  'Differentiated Instruction',
  'Pembelajaran Berbasis Literasi',
  'Pembelajaran Berbasis Numerasi',
  'Teaching at the Right Level (TaRL)',
  'Culturally Responsive Teaching',
];

// 8 Kompetensi Profil Lulusan sesuai Permendikdasmen No. 13 Tahun 2025 (Pasal 17)
const PROFIL_PELAJAR_OPTIONS = [
  { value: 'Beriman dan Bertakwa kepada Tuhan Yang Maha Esa', short: 'Beriman & Bertakwa' },
  { value: 'Kewargaan', short: 'Kewargaan' },
  { value: 'Penalaran Kritis', short: 'Penalaran Kritis' },
  { value: 'Kreativitas', short: 'Kreativitas' },
  { value: 'Kolaborasi', short: 'Kolaborasi' },
  { value: 'Kemandirian', short: 'Kemandirian' },
  { value: 'Kesehatan', short: 'Kesehatan' },
  { value: 'Komunikasi', short: 'Komunikasi' },
];

const STEPS = [
  { num: 1, label: 'Jenis Dokumen', desc: 'Pilih dokumen yang ingin dibuat' },
  { num: 2, label: 'Detail Kelas', desc: 'Jenjang, kelas & mata pelajaran' },
  { num: 3, label: 'Pedagogis & AI', desc: 'Topik, praktik & profil pelajar' },
  { num: 4, label: 'Review & Generate', desc: 'Periksa & mulai proses AI' },
];

// Tahap-tahap yang ditampilkan saat loading
const GENERATE_STAGES = {
  'Modul Ajar': [
    { pct: 5,  label: 'Mempersiapkan konteks Modul Ajar...' },
    { pct: 15, label: 'Membaca Capaian Pembelajaran (CP) fase ini...' },
    { pct: 28, label: 'Menyusun Tujuan & Alur Pembelajaran...' },
    { pct: 43, label: 'Merancang kegiatan tiap pertemuan...' },
    { pct: 58, label: 'Menyusun materi pokok & LKPD...' },
    { pct: 72, label: 'Merancang asesmen formatif & sumatif...' },
    { pct: 84, label: 'Mengintegrasikan Profil Pelajar Pancasila...' },
    { pct: 95, label: 'Hampir selesai, menyimpan dokumen...' },
  ],
  'KKTP': [
    { pct: 8,  label: 'Mempersiapkan konteks KKTP...' },
    { pct: 22, label: 'Membaca Capaian Pembelajaran (CP) fase ini...' },
    { pct: 40, label: 'Menyusun indikator ketercapaian...' },
    { pct: 58, label: 'Merancang rubrik & kriteria penilaian...' },
    { pct: 75, label: 'Menyesuaikan dengan Profil Pelajar Pancasila...' },
    { pct: 88, label: 'Memfinalisasi tabel KKTP...' },
    { pct: 95, label: 'Hampir selesai, menyimpan dokumen...' },
  ],
  'Promes': [
    { pct: 8,  label: 'Mempersiapkan konteks Program Semester...' },
    { pct: 25, label: 'Membaca Capaian Pembelajaran semester ini...' },
    { pct: 42, label: 'Menyusun distribusi materi per minggu...' },
    { pct: 60, label: 'Menghitung alokasi waktu & JP...' },
    { pct: 78, label: 'Menyusun kalender kegiatan semester...' },
    { pct: 95, label: 'Hampir selesai, menyimpan Promes...' },
  ],
  'Prota': [
    { pct: 8,  label: 'Mempersiapkan konteks Program Tahunan...' },
    { pct: 25, label: 'Membaca Capaian Pembelajaran tahunan...' },
    { pct: 45, label: 'Membagi materi ke dua semester...' },
    { pct: 65, label: 'Menghitung total alokasi waktu tahunan...' },
    { pct: 82, label: 'Menyusun struktur Prota per semester...' },
    { pct: 95, label: 'Hampir selesai, menyimpan Prota...' },
  ],
  'Jadwal': [
    { pct: 10, label: 'Mempersiapkan konteks jadwal pelajaran...' },
    { pct: 30, label: 'Menyusun distribusi jam mengajar...' },
    { pct: 55, label: 'Mengatur alokasi waktu per mata pelajaran...' },
    { pct: 78, label: 'Memfinalisasi struktur jadwal...' },
    { pct: 95, label: 'Hampir selesai, menyimpan Jadwal...' },
  ],
};

export const DocumentGenerator = () => {
  const [step, setStep] = useState(1);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [success, setSuccess] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [coinBalance, setCoinBalance] = useState(null);
  const [coinError, setCoinError] = useState(null);
  const [genError, setGenError] = useState(null);
  const navigate = useNavigate();

  // Pesan berputar saat AI masih generate (setelah 95%)
  const WAITING_MESSAGES = [
    'AI sedang menulis langkah pembelajaran...',
    'Menyusun rubrik asesmen...',
    'Membuat LKPD untuk siswa...',
    'Menulis refleksi & glosarium...',
    'Finalisasi seluruh komponen modul...',
    'Hampir selesai, mohon sabar...',
  ];

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coins/balance`, { withCredentials: true })
      .then(r => setCoinBalance(r.data.coin_balance))
      .catch(() => {});
  }, []);

  const [formData, setFormData] = useState({
    document_type: '',
    jenjang: 'SMP',
    kelas: 7,
    subject_id: '',
    semester: 'Ganjil',
    tahun_ajaran: '2025/2026',
    elemen: '',
    topik: '',
    pertemuan_ke: 1,
    jumlah_pertemuan: 1,
    alokasi_waktu: 40,
    hari: '',
    jam_mulai: '07:00',
    praktik_pedagogis: [],
    profil_pelajar: [],
    custom_notes: '',
  });

  // Field yang tampil per jenis dokumen
  const DOC_FIELDS = {
    'Modul Ajar': { semester: true,  topik: true,  pertemuan_ke: true, alokasi: true,  pedagogis: true,  profil: true,  jadwal: false, prota: false },
    'KKTP':       { semester: true,  topik: true,  pertemuan: false, alokasi: false, pedagogis: false, profil: true,  jadwal: false, prota: false },
    'Promes':     { semester: true,  topik: false, pertemuan: false, alokasi: true,  pedagogis: false, profil: false, jadwal: false, prota: false },
    'Prota':      { semester: false, topik: false, pertemuan: false, alokasi: true,  pedagogis: false, profil: false, jadwal: false, prota: true  },
    'Jadwal':     { semester: true,  topik: false, pertemuan: false, alokasi: true,  pedagogis: false, profil: false, jadwal: true,  prota: false },
  };
  const fields = DOC_FIELDS[formData.document_type] || {};

  const toggleArray = (key, val) => {
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(val)
        ? prev[key].filter(v => v !== val)
        : [...prev[key], val],
    }));
  };

  useEffect(() => {
    loadSubjects();
  }, [formData.jenjang, formData.kelas]);

  const loadSubjects = async () => {
    try {
      const { data } = await axios.get(`${API}/subjects?jenjang=${formData.jenjang}&kelas=${formData.kelas}`, { withCredentials: true });
      setSubjects(data);
      setFormData(prev => ({ ...prev, subject_id: data[0]?._id || '' }));
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };

  const handleJenjangChange = (val) => {
    const kelas = JENJANG_KELAS[val][0];
    setFormData(prev => ({ ...prev, jenjang: val, kelas }));
  };

  const handleGenerate = async () => {
    const stages = GENERATE_STAGES[formData.document_type] || GENERATE_STAGES['Modul Ajar'];
    setLoading(true);
    setGenError(null);
    setCoinError(null);
    setProgress(0);
    setElapsed(0);
    setProgressLabel(stages[0].label);

    // Timer elapsed detik
    const startTime = Date.now();
    const elapsedInterval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    // Simulasi progress — maju pelan, berhenti di 95% sampai response datang
    let stageIdx = 0;
    let waitingMsgIdx = 0;
    const interval = setInterval(() => {
      if (stageIdx < stages.length - 1) {
        stageIdx += 1;
        setProgress(stages[stageIdx].pct);
        setProgressLabel(stages[stageIdx].label);
      } else {
        // Sudah di 95% — putar pesan menunggu & creep progress
        waitingMsgIdx = (waitingMsgIdx + 1) % WAITING_MESSAGES.length;
        setProgressLabel(WAITING_MESSAGES[waitingMsgIdx]);
        // Progress creep lambat dari 95 → 99
        setProgress(prev => prev < 99 ? Math.round((prev + 0.3) * 10) / 10 : 99);
      }
    }, 2500);

    try {
      const { data } = await axios.post(`${API}/documents/generate`, formData, { withCredentials: true });
      clearInterval(interval);
      clearInterval(elapsedInterval);
      setProgress(100);
      setProgressLabel('Dokumen berhasil dibuat! ✓');
      await new Promise(r => setTimeout(r, 700));
      setGeneratedDoc(data);
      setSuccess(true);
    } catch (error) {
      clearInterval(interval);
      clearInterval(elapsedInterval);
      const detail = error.response?.data?.detail;
      console.error('Generate error:', error.response?.status, detail);
      if (error.response?.status === 402 && detail?.code === 'INSUFFICIENT_COINS') {
        setCoinError(detail);
      } else {
        const msg = typeof detail === 'string' ? detail
          : detail?.message || JSON.stringify(detail) || error.message || 'Terjadi kesalahan tidak diketahui.';
        setGenError(msg);
      }
    } finally {
      setLoading(false);
      setProgress(0);
      setElapsed(0);
    }
  };

  const selectedDocType = DOC_TYPES.find(d => d.value === formData.document_type);
  const selectedSubject = subjects.find(s => s._id === formData.subject_id);

  // ── COIN ERROR STATE ──
  if (coinError) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[70vh]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-2xl bg-yellow-50 flex items-center justify-center mx-auto mb-5">
              <CoinIcon size={48} />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Coin Guru Tidak Cukup
            </h2>
            <p className="text-slate-500 text-sm mb-1">{coinError.message}</p>
            <p className="text-slate-400 text-xs mb-7">
              Isi ulang Coin Guru Anda untuk melanjutkan membuat dokumen.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/topup')}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm shadow-md hover:-translate-y-0.5 transition-all"
                style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>
                <CoinIcon size={16} /> Isi Coin Guru Sekarang
              </button>
              <button
                onClick={() => setCoinError(null)}
                className="px-6 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
                Kembali
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── GENERATION ERROR STATE ──
  if (genError) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[70vh]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="text-center max-w-lg">
            <div className="w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
              <span className="text-4xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Gagal Membuat Dokumen
            </h2>
            <p className="text-slate-500 text-sm mb-2">
              Terjadi kesalahan saat AI menyusun dokumen. <strong>Coin Guru Anda tidak dipotong.</strong>
            </p>
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-6 text-left">
              <p className="text-red-700 text-xs font-mono leading-relaxed break-words">{genError}</p>
            </div>
            <p className="text-slate-400 text-xs mb-6">
              Anda bisa mencoba lagi — coba ubah topik, tambahkan catatan, atau kurangi kompleksitas permintaan.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => { setGenError(null); setStep(4); }}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm shadow-md hover:-translate-y-0.5 transition-all"
                style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                🔄 Coba Lagi
              </button>
              <button
                onClick={() => { setGenError(null); setStep(1); }}
                className="px-6 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
                Mulai Dari Awal
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── LOADING STATE ──
  if (loading) {
    const isDone = progress === 100;
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[70vh]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="w-full max-w-md text-center">

            {/* Ikon animasi */}
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-500 ${
              isDone ? 'bg-green-100' : 'bg-blue-50'
            }`}
              style={isDone ? {} : { background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)' }}>
              {isDone ? (
                <CheckCircle size={40} className="text-green-500" />
              ) : (
                <div className="relative w-10 h-10">
                  <svg className="animate-spin w-10 h-10 text-blue-200" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  </svg>
                  <svg className="animate-spin w-10 h-10 absolute inset-0" style={{ animationDuration: '0.9s' }} fill="none" viewBox="0 0 24 24">
                    <path className="text-blue-600" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Judul */}
            <h2 className="text-xl font-bold mb-1"
              style={{ fontFamily: "'Outfit', sans-serif", color: isDone ? '#16a34a' : '#1E3A8A' }}>
              {isDone ? 'Dokumen Selesai!' : `Membuat ${formData.document_type}...`}
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              {isDone ? 'Menyimpan dan mempersiapkan unduhan...' : `${selectedSubject?.name} · Kelas ${formData.kelas} ${formData.jenjang}`}
            </p>

            {/* Progress bar */}
            <div className="relative mb-3">
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${progress}%`,
                    background: isDone
                      ? 'linear-gradient(90deg, #16a34a, #22c55e)'
                      : 'linear-gradient(90deg, #1E3A8A, #3B82F6, #60A5FA)',
                    backgroundSize: isDone ? '100%' : '200% 100%',
                    animation: isDone ? 'none' : 'shimmer 1.8s linear infinite',
                  }}
                />
              </div>
              {/* Persentase */}
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-slate-400 font-medium">{progressLabel}</span>
                <span className={`text-sm font-bold tabular-nums ${isDone ? 'text-green-600' : 'text-blue-700'}`}>
                  {Math.round(progress)}%
                </span>
              </div>
            </div>

            {/* Tahapan mini */}
            <div className="mt-6 bg-white border border-slate-100 rounded-2xl p-4 text-left shadow-sm">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Tahapan Generate</div>
              <div className="flex flex-col gap-2">
                {(GENERATE_STAGES[formData.document_type] || GENERATE_STAGES['Modul Ajar']).map((s, i) => {
                  const done = progress >= s.pct;
                  const active = progressLabel === s.label;
                  return (
                    <div key={i} className={`flex items-center gap-2.5 transition-all duration-300 ${done ? 'opacity-100' : 'opacity-30'}`}>
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        done
                          ? active
                            ? 'bg-blue-500 ring-2 ring-blue-200'
                            : 'bg-green-500'
                          : 'bg-slate-200'
                      }`}>
                        {done && !active && (
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <path d="M1.5 4L3.2 5.5L6.5 2.5" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <span className={`text-xs ${active ? 'text-blue-700 font-semibold' : done ? 'text-slate-600' : 'text-slate-400'}`}>
                        {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mt-4">
              <span className="text-xs text-slate-400">Mohon tunggu, jangan tutup halaman ini</span>
              {elapsed > 0 && (
                <span className="text-xs font-mono bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                  {Math.floor(elapsed / 60) > 0 ? `${Math.floor(elapsed/60)}m ` : ''}{elapsed % 60}s
                </span>
              )}
            </div>
            {progress >= 94 && progress < 100 && (
              <p className="text-xs text-blue-500 mt-2 font-medium animate-pulse">
                AI sedang menulis dokumen lengkap — proses ini normal membutuhkan 30–60 detik
              </p>
            )}
          </div>
        </div>
        <style>{`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </DashboardLayout>
    );
  }

  // ── SUCCESS STATE ──
  if (success) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[70vh]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div className="text-center max-w-md">
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-blue-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Dokumen Berhasil Dibuat! 🎉
            </h2>
            <p className="text-slate-500 mb-2">
              <span className="font-semibold text-blue-700">{formData.document_type}</span> untuk{' '}
              <span className="font-semibold">{selectedSubject?.name}</span> Kelas {formData.kelas}{' '}
              {formData.jenjang} sudah siap.
            </p>
            <p className="text-slate-400 text-sm mb-8">AI telah menyusun dokumen sesuai Permendikdasmen No. 13 Tahun 2025 dengan pendekatan Pembelajaran Mendalam.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${generatedDoc?._id}/download`}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                style={{ background: 'linear-gradient(135deg, #059669, #10B981)' }}>
                <Download size={16} />
                Download Word (.docx)
              </a>
              <button
                onClick={() => navigate('/documents')}
                className="px-6 py-3 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                Lihat Dokumen
              </button>
              <button
                onClick={() => { setSuccess(false); setStep(1); setFormData(prev => ({ ...prev, document_type: '', topik: '', praktik_pedagogis: [], profil_pelajar: [], custom_notes: '' })); }}
                className="px-6 py-3 rounded-xl border-2 border-blue-200 text-blue-700 font-semibold text-sm hover:bg-blue-50 transition-colors">
                Buat Lagi
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="document-generator-page" className="max-w-4xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Buat Dokumen Baru
          </h1>
          <p className="text-slate-500 text-sm mt-1">AI akan menyusun dokumen sesuai Permendikdasmen No. 13 Tahun 2025 secara otomatis</p>
        </div>

        {/* Step indicator */}
        <div className="bg-white border border-slate-100 rounded-2xl p-4 mb-6 shadow-sm">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className={`flex items-center gap-2.5 ${step === s.num ? 'flex-shrink-0' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all ${
                    step > s.num
                      ? 'bg-green-500 text-white'
                      : step === s.num
                      ? 'text-white shadow-md'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                  style={step === s.num ? { background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' } : {}}>
                    {step > s.num ? <CheckCircle size={16} /> : s.num}
                  </div>
                  <div className="hidden sm:block">
                    <div className={`text-xs font-semibold ${step === s.num ? 'text-blue-900' : step > s.num ? 'text-green-600' : 'text-slate-400'}`}>
                      {s.label}
                    </div>
                    <div className="text-xs text-slate-400 hidden md:block">{s.desc}</div>
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-3 rounded-full transition-all ${step > s.num ? 'bg-green-300' : 'bg-slate-100'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">

          {/* Main form */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">

              {/* ── STEP 1: Pilih Jenis Dokumen ── */}
              {step === 1 && (
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>1</div>
                    <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Pilih Jenis Dokumen</h2>
                  </div>
                  <p className="text-sm text-slate-500 mb-5">Klik dokumen yang ingin Anda buat. AI akan menyesuaikan struktur dan konten secara otomatis.</p>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {DOC_TYPES.map(({ value, label, fullName, desc, icon: Icon, color, time }) => {
                      const c = COLOR_MAP[color];
                      const selected = formData.document_type === value;
                      return (
                        <button
                          key={value}
                          onClick={() => setFormData(prev => ({ ...prev, document_type: value }))}
                          className={`text-left p-4 rounded-xl border-2 transition-all hover:shadow-md ${
                            selected ? `${c.card} shadow-md` : 'border-slate-100 bg-white hover:border-slate-200'
                          }`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${selected ? c.icon : 'bg-slate-100 text-slate-400'}`}>
                              <Icon size={20} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2 mb-0.5">
                                <span className={`font-bold text-sm ${selected ? 'text-slate-800' : 'text-slate-700'}`}>{label}</span>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className="text-xs bg-yellow-50 border border-yellow-200 text-yellow-700 px-1.5 py-0.5 rounded-full font-semibold">
                                    <span className="inline-flex items-center gap-0.5"><CoinIcon size={11} /> {COIN_COST[value]}</span>
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${selected ? c.badge : 'bg-slate-100 text-slate-400'}`}>
                                    {time}
                                  </span>
                                </div>
                              </div>
                              <div className="text-xs text-slate-400 font-medium mb-1">{fullName}</div>
                              <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                            </div>
                          </div>
                          {selected && (
                            <div className="flex items-center gap-1 mt-3 pt-3 border-t border-current/20 text-xs font-semibold" style={{ borderColor: 'currentColor', opacity: 0.3 }}>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {!formData.document_type && (
                    <div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl text-blue-600 text-xs">
                      <Info size={14} className="flex-shrink-0" />
                      Pilih salah satu jenis dokumen di atas untuk melanjutkan.
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 2: Detail Kelas ── */}
              {step === 2 && (
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>2</div>
                    <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Detail Kelas & Mata Pelajaran</h2>
                  </div>

                  <div className="flex flex-col gap-5">
                    {/* Jenjang & Kelas */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700 font-semibold text-sm mb-2 block">Jenjang</Label>
                        <Select value={formData.jenjang} onValueChange={handleJenjangChange}>
                          <SelectTrigger className="rounded-xl border-blue-100 h-11" data-testid="jenjang-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['SD', 'SMP', 'SMA', 'SMK'].map(j => (
                              <SelectItem key={j} value={j}>{j}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-400 mt-1">
                          {formData.jenjang === 'SD' ? 'Kelas 1–6' : formData.jenjang === 'SMP' ? 'Kelas 7–9' : 'Kelas 10–12'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-700 font-semibold text-sm mb-2 block">Kelas</Label>
                        <Select value={String(formData.kelas)} onValueChange={(v) => setFormData(prev => ({ ...prev, kelas: parseInt(v) }))}>
                          <SelectTrigger className="rounded-xl border-blue-100 h-11" data-testid="kelas-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(JENJANG_KELAS[formData.jenjang] || []).map(k => (
                              <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Fase Otomatis */}
                    {(() => {
                      const fase = getFase(formData.jenjang, formData.kelas);
                      return (
                        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-100">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold text-lg"
                            style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                            {fase}
                          </div>
                          <div>
                            <div className="text-xs font-bold text-blue-800 leading-tight">
                              Fase {fase} — Kurikulum Merdeka
                            </div>
                            <div className="text-xs text-blue-600 mt-0.5">
                              {FASE_DESC[fase]} · CP disusun per fase, bukan per kelas
                            </div>
                          </div>
                          <div className="ml-auto flex-shrink-0">
                            <span className="text-[10px] bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded-full border border-blue-200">
                              Otomatis
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Mata Pelajaran */}
                    <div>
                      <Label className="text-slate-700 font-semibold text-sm mb-2 block">Mata Pelajaran</Label>
                      <Select value={formData.subject_id} onValueChange={(v) => setFormData(prev => ({ ...prev, subject_id: v }))}>
                        <SelectTrigger className="rounded-xl border-blue-100 h-11" data-testid="subject-select">
                          <SelectValue placeholder="Pilih mata pelajaran..." />
                        </SelectTrigger>
                        <SelectContent>
                          {subjects.map((s) => (
                            <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {subjects.length === 0 && (
                        <p className="text-xs text-orange-500 mt-1">⚠ Belum ada mata pelajaran untuk jenjang ini. Hubungi admin.</p>
                      )}
                    </div>

                    {/* Semester & Tahun Ajaran */}
                    {fields.semester !== false && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">Semester</Label>
                          <Select value={formData.semester} onValueChange={(v) => setFormData(prev => ({ ...prev, semester: v }))}>
                            <SelectTrigger className="rounded-xl border-blue-100 h-11" data-testid="semester-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Ganjil">Ganjil (1)</SelectItem>
                              <SelectItem value="Genap">Genap (2)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">Tahun Ajaran</Label>
                          <Input
                            value={formData.tahun_ajaran}
                            onChange={(e) => setFormData(prev => ({ ...prev, tahun_ajaran: e.target.value }))}
                            data-testid="tahun-ajaran-input"
                            placeholder="2025/2026"
                            className="rounded-xl border-blue-100 h-11"
                          />
                        </div>
                      </div>
                    )}

                    {/* Prota: hanya tahun ajaran */}
                    {fields.prota && (
                      <div>
                        <Label className="text-slate-700 font-semibold text-sm mb-2 block">Tahun Ajaran</Label>
                        <Input
                          value={formData.tahun_ajaran}
                          onChange={(e) => setFormData(prev => ({ ...prev, tahun_ajaran: e.target.value }))}
                          placeholder="2025/2026"
                          className="rounded-xl border-blue-100 h-11"
                        />
                      </div>
                    )}

                    {/* Alokasi Waktu (Promes, Prota, Jadwal) */}
                    {fields.alokasi && !fields.pertemuan_ke && (
                      <div>
                        <Label className="text-slate-700 font-semibold text-sm mb-2 block">
                          Alokasi Waktu per JP <span className="text-slate-400 font-normal">(menit)</span>
                        </Label>
                        <Select value={String(formData.alokasi_waktu)} onValueChange={(v) => setFormData(prev => ({ ...prev, alokasi_waktu: parseInt(v) }))}>
                          <SelectTrigger className="rounded-xl border-blue-100 h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[30, 35, 40, 45, 50].map(m => (
                              <SelectItem key={m} value={String(m)}>{m} menit / JP</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Jadwal: hari & jam */}
                    {fields.jadwal && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">Hari</Label>
                          <Select value={formData.hari} onValueChange={(v) => setFormData(prev => ({ ...prev, hari: v }))}>
                            <SelectTrigger className="rounded-xl border-blue-100 h-11">
                              <SelectValue placeholder="Pilih hari..." />
                            </SelectTrigger>
                            <SelectContent>
                              {['Senin','Selasa','Rabu','Kamis','Jumat'].map(h => (
                                <SelectItem key={h} value={h}>{h}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">Jam Mulai</Label>
                          <Input
                            type="time"
                            value={formData.jam_mulai}
                            onChange={(e) => setFormData(prev => ({ ...prev, jam_mulai: e.target.value }))}
                            className="rounded-xl border-blue-100 h-11"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── STEP 3: Konteks Dokumen (adaptif) ── */}
              {step === 3 && (
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>3</div>
                    <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {fields.pedagogis ? 'Konteks Pedagogis' : 'Detail Tambahan'}
                    </h2>
                  </div>

                  <div className="flex flex-col gap-6">

                    {/* Topik (RPP, Modul Ajar, KKTP) */}
                    {fields.topik && (
                      <div>
                        <Label className="text-slate-700 font-semibold text-sm mb-1 block">
                          Topik / Elemen / Materi Pokok
                          <span className="text-slate-400 font-normal ml-1">(opsional)</span>
                        </Label>
                        <Input
                          value={formData.topik}
                          onChange={(e) => setFormData(prev => ({ ...prev, topik: e.target.value }))}
                          placeholder="Contoh: Bilangan Bulat, Teks Argumentasi, Fotosintesis, SPLDV..."
                          className="rounded-xl border-blue-100 h-11"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Kosongkan jika ingin AI menentukan sendiri berdasarkan CP fase.
                        </p>
                      </div>
                    )}

                    {/* Pertemuan ke- & Alokasi (Modul Ajar) */}
                    {fields.pertemuan_ke && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">Pertemuan ke-</Label>
                          <Select value={String(formData.pertemuan_ke)} onValueChange={(v) => setFormData(prev => ({ ...prev, pertemuan_ke: parseInt(v) }))}>
                            <SelectTrigger className="rounded-xl border-blue-100 h-11">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                <SelectItem key={n} value={String(n)}>Pertemuan {n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-slate-700 font-semibold text-sm mb-2 block">
                            Alokasi Waktu <span className="text-slate-400 font-normal">(menit/JP)</span>
                          </Label>
                          <Select value={String(formData.alokasi_waktu)} onValueChange={(v) => setFormData(prev => ({ ...prev, alokasi_waktu: parseInt(v) }))}>
                            <SelectTrigger className="rounded-xl border-blue-100 h-11">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[30,35,40,45,50].map(m => (
                                <SelectItem key={m} value={String(m)}>{m} menit</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {/* Praktik Pedagogis (RPP, Modul Ajar) */}
                    {fields.pedagogis && (
                      <div>
                        <Label className="text-slate-700 font-semibold text-sm mb-2 block">
                          Praktik Pedagogis
                          <span className="text-slate-400 font-normal ml-1">(opsional)</span>
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          {PRAKTIK_PEDAGOGIS_OPTIONS.map(pp => {
                            const selected = formData.praktik_pedagogis.includes(pp);
                            return (
                              <button key={pp} type="button" onClick={() => toggleArray('praktik_pedagogis', pp)}
                                className={`text-left px-3 py-2.5 rounded-xl border-2 text-xs font-medium transition-all ${
                                  selected ? 'border-blue-400 bg-blue-50 text-blue-800' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-200'
                                }`}>
                                <span className={`mr-1.5 ${selected ? 'text-blue-500' : 'text-slate-300'}`}>{selected ? '✓' : '○'}</span>
                                {pp}
                              </button>
                            );
                          })}
                        </div>
                        {formData.praktik_pedagogis.length === 0 && (
                          <p className="text-xs text-slate-400 mt-2">AI akan memilih yang paling sesuai jika tidak dipilih.</p>
                        )}
                      </div>
                    )}

                    {/* Profil Pelajar Pancasila (RPP, Modul Ajar, KKTP) */}
                    {fields.profil && (
                      <div>
                        <Label className="text-slate-700 font-semibold text-sm mb-1 block">
                          Profil Lulusan
                          <span className="text-slate-400 font-normal ml-1">(opsional)</span>
                        </Label>
                        <p className="text-xs text-slate-400 mb-3">8 kompetensi sesuai Permendikdasmen No. 13 Tahun 2025.</p>
                        <div className="grid grid-cols-2 gap-2">
                          {PROFIL_PELAJAR_OPTIONS.map(({ value, short }) => {
                            const selected = formData.profil_pelajar.includes(value);
                            return (
                              <button key={value} type="button" onClick={() => toggleArray('profil_pelajar', value)}
                                className={`text-left px-3 py-2.5 rounded-xl border-2 transition-all ${
                                  selected ? 'border-green-400 bg-green-50 text-green-800' : 'border-slate-200 bg-white text-slate-600 hover:border-green-200'
                                }`}>
                                <div className={`text-xs font-bold mb-0.5 ${selected ? 'text-green-600' : 'text-slate-400'}`}>{selected ? '✓' : '○'} {short}</div>
                                <div className="text-[10px] text-slate-400 leading-tight">{value}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Catatan Tambahan — selalu tampil */}
                    <div>
                      <Label className="text-slate-700 font-semibold text-sm mb-1 block">
                        Catatan Tambahan untuk AI <span className="text-slate-400 font-normal">(opsional)</span>
                      </Label>
                      <Textarea
                        value={formData.custom_notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, custom_notes: e.target.value }))}
                        data-testid="custom-notes-textarea"
                        placeholder={
                          formData.document_type === 'Promes' ? 'Contoh: Sekolah kami libur minggu ke-3 tiap bulan, tambahkan keterangan ulangan harian...' :
                          formData.document_type === 'Prota'  ? 'Contoh: Sekolah kami menggunakan sistem blok, tambahkan keterangan kegiatan sekolah...' :
                          formData.document_type === 'Jadwal' ? 'Contoh: Kelas ini gabungan IPA-IPS, siswa berjumlah 34 orang...' :
                          'Contoh: Sekolah kami menggunakan pendekatan STEAM, siswa rata-rata level menengah...'
                        }
                        rows={3}
                        className="rounded-xl border-blue-100 text-sm resize-none"
                      />
                      <p className="text-xs text-slate-400 mt-1">Instruksi khusus agar dokumen lebih sesuai konteks sekolah Anda.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── STEP 4: Review & Generate ── */}
              {step === 4 && (
                <div>
                  <div className="flex items-center gap-2 mb-5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>4</div>
                    <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Review & Generate</h2>
                  </div>

                  {/* Summary */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5 mb-5">
                    <div className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-3">Ringkasan Dokumen</div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                      {[
                        { label: 'Jenis Dokumen', value: formData.document_type },
                        { label: 'Jenjang & Kelas', value: `${formData.jenjang} – Kelas ${formData.kelas}` },
                        { label: 'Fase Kurikulum Merdeka', value: `Fase ${getFase(formData.jenjang, formData.kelas)} · ${FASE_DESC[getFase(formData.jenjang, formData.kelas)]}` },
                        { label: 'Mata Pelajaran', value: selectedSubject?.name || '-' },
                        ...(fields.semester !== false ? [{ label: 'Semester', value: `${formData.semester} ${formData.tahun_ajaran}` }] : [{ label: 'Tahun Ajaran', value: formData.tahun_ajaran }]),
                        ...(fields.topik && formData.topik ? [{ label: 'Topik / Materi', value: formData.topik }] : []),
                        ...(fields.pertemuan_ke ? [{ label: 'Pertemuan', value: `Pertemuan ${formData.pertemuan_ke} — ${formData.alokasi_waktu} menit/JP` }] : []),
                        ...(fields.jadwal && formData.hari ? [{ label: 'Jadwal', value: `${formData.hari}, ${formData.jam_mulai}` }] : []),
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <div className="text-xs text-slate-400">{label}</div>
                          <div className="text-sm font-semibold text-slate-800">{value}</div>
                        </div>
                      ))}
                    </div>

                    {formData.praktik_pedagogis.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-100">
                        <div className="text-xs text-slate-400 mb-1.5">Praktik Pedagogis</div>
                        <div className="flex flex-wrap gap-1.5">
                          {formData.praktik_pedagogis.map(p => (
                            <span key={p} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {formData.profil_pelajar.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-100">
                        <div className="text-xs text-slate-400 mb-1.5">Profil Lulusan (Permendikdasmen 13/2025)</div>
                        <div className="flex flex-wrap gap-1.5">
                          {formData.profil_pelajar.map(p => (
                            <span key={p} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              {PROFIL_PELAJAR_OPTIONS.find(o => o.value === p)?.short || p}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Coin cost info */}
                  {formData.document_type && (
                    <div className={`flex items-center justify-between p-4 rounded-xl border mb-3 ${
                      coinBalance !== null && coinBalance < COIN_COST[formData.document_type]
                        ? 'bg-red-50 border-red-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        <CoinIcon size={22} />
                        <div>
                          <div className="text-sm font-bold text-slate-800">
                            Biaya: {COIN_COST[formData.document_type]} Coin Guru
                            <span className="text-slate-400 font-normal ml-1 text-xs">
                              (Rp {new Intl.NumberFormat('id-ID').format(COIN_COST[formData.document_type] * 2000)})
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            Saldo Anda: <strong>{coinBalance ?? '...'} koin</strong>
                          </div>
                        </div>
                      </div>
                      {coinBalance !== null && coinBalance < COIN_COST[formData.document_type] && (
                        <button onClick={() => navigate('/topup')}
                          className="text-xs font-bold text-white px-3 py-1.5 rounded-lg flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>
                          + Isi Koin
                        </button>
                      )}
                    </div>
                  )}

                  {/* Estimated time */}
                  <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Zap size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-blue-800">Estimasi waktu: {selectedDocType?.time || '~3 menit'}</div>
                      <div className="text-xs text-blue-600">AI akan menyusun dokumen sesuai Permendikdasmen No. 13 Tahun 2025.</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between mt-6 pt-5 border-t border-slate-100">
                <button
                  onClick={() => setStep(s => s - 1)}
                  disabled={step === 1}
                  data-testid="wizard-back-button"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm hover:border-blue-300 hover:text-blue-700 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={17} /> Kembali
                </button>

                {step < 4 ? (
                  <button
                    onClick={() => setStep(s => s + 1)}
                    disabled={step === 1 && !formData.document_type}
                    data-testid="wizard-next-button"
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
                  >
                    Selanjutnya <ChevronRight size={17} />
                  </button>
                ) : (
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !formData.subject_id || (coinBalance !== null && coinBalance < COIN_COST[formData.document_type])}
                    data-testid="generate-button"
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-bold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    style={{ background: loading ? '#94A3B8' : 'linear-gradient(135deg, #F59E0B, #EF4444)' }}
                  >
                    <><Zap size={17} /> Generate Dokumen</>

                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right: Info sidebar */}
          <div className="flex flex-col gap-4">

            {/* Selected doc type preview */}
            {selectedDocType && (
              <div className={`rounded-2xl p-5 border-2 ${COLOR_MAP[selectedDocType.color].card}`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${COLOR_MAP[selectedDocType.color].icon}`}>
                  <selectedDocType.icon size={22} />
                </div>
                <div className="font-bold text-slate-800 text-sm mb-1">{selectedDocType.label}</div>
                <div className="text-xs text-slate-500 mb-3 leading-relaxed">{selectedDocType.desc}</div>
                <div className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${COLOR_MAP[selectedDocType.color].badge}`}>
                  <Clock size={11} /> {selectedDocType.time}
                </div>
              </div>
            )}

            {/* Pilihan info jika belum pilih */}
            {!selectedDocType && (
              <div className="rounded-2xl p-5 bg-slate-50 border border-slate-100">
                <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                  <FileText size={22} className="text-slate-400" />
                </div>
                <div className="font-bold text-slate-400 text-sm mb-1">Belum dipilih</div>
                <div className="text-xs text-slate-400">Pilih jenis dokumen untuk melihat informasi detail.</div>
              </div>
            )}

            {/* Tips */}
            <div className="rounded-2xl p-5 bg-white border border-blue-100 shadow-sm">
              <div className="font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
                <Info size={15} className="text-blue-500" /> Tips AI
              </div>
              <ul className="flex flex-col gap-2.5">
                {[
                  'Semakin spesifik catatan Anda, semakin relevan hasilnya',
                  'Isi elemen/topik untuk fokus pada materi tertentu',
                  'Dokumen dapat diedit setelah di-generate',
                  'Hasil dapat di-download dalam format Word',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
                    <CheckCircle size={12} className="text-green-500 flex-shrink-0 mt-0.5" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {/* Kurikulum badge */}
            <div className="rounded-2xl p-4 text-center border border-green-100 bg-green-50">
              <div className="text-xs font-bold text-green-700 mb-1">✅ Sesuai Regulasi Terbaru</div>
              <div className="text-xs text-green-600">Permendikdasmen No. 13 Tahun 2025</div>
              <div className="text-[10px] text-green-500 mt-0.5">Berlaku 15 Juli 2025</div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
